import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const SEVERITY = {
  error: { tone: "error", Icon: DeleteIcon },
  warning: { tone: "warning", Icon: WarningAmberIcon },
  info: { tone: "primary", Icon: WarningAmberIcon },
};

// The one confirmation dialog. `consequences` lists what the action breaks;
// `blocked` means it can't proceed, so only the dismiss button renders.
export default function ConfirmDialog({
  open,
  title = "Confirm Action",
  subtitle = "Are you sure?",
  description,
  message,
  consequences = [],
  confirmText = "Confirm",
  confirmLabel,
  cancelText = "Cancel",
  loadingLabel,
  blocked = false,
  onConfirm,
  onClose,
  onCancel,
  loading = false,
  severity = "error", // "error" (red/delete) | "warning" (amber) | "info" (indigo)
}) {
  const { tone, Icon } = SEVERITY[severity] || SEVERITY.error;
  const dismiss = onClose || onCancel;
  const body = description ?? message;
  const confirmCopy = confirmLabel ?? confirmText;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : dismiss}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 10px 25px rgba(15,23,42,0.10)",
        },
      }}
    >
      <DialogTitle
        id="confirm-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 1,
          fontWeight: 600,
          color: "text.primary",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            backgroundColor: `${tone}.light`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: `${tone}.main`, fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 1, pb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {body}
        </Typography>
        {consequences.length > 0 && (
          <Alert severity={blocked ? "error" : "warning"} sx={{ mt: 2 }}>
            <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
              {consequences.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 1, gap: 1 }}>
        <Button
          onClick={dismiss}
          disabled={loading}
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            fontSize: "11.5px",
            fontWeight: 600,
            color: "text.secondary",
            borderColor: "grey.300",
            borderRadius: "6px",
            px: 2,
            py: 0.6,
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          {blocked ? "Close" : cancelText}
        </Button>
        {!blocked && (
          <Button
            onClick={onConfirm}
            disabled={loading}
            variant="contained"
            size="small"
            color={tone}
            startIcon={loading ? <CircularProgress size={12} color="inherit" /> : null}
            sx={{
              textTransform: "none",
              fontSize: "11.5px",
              fontWeight: 600,
              borderRadius: "6px",
              px: 2,
              py: 0.6,
            }}
          >
            {loading && loadingLabel ? loadingLabel : confirmCopy}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

ConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  description: PropTypes.node,
  message: PropTypes.node,
  consequences: PropTypes.arrayOf(PropTypes.string),
  confirmText: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelText: PropTypes.string,
  loadingLabel: PropTypes.string,
  blocked: PropTypes.bool,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
  onCancel: PropTypes.func,
  loading: PropTypes.bool,
  severity: PropTypes.oneOf(["error", "warning", "info"]),
};
